import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EnvelopeSimple, Phone, GoogleLogo, TwitterLogo, AppleLogo, Eye, EyeSlash, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const { login, register, sendOtp, verifyOtp, socialAuth } = useAuth();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Email form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  // Phone form state
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneName, setPhoneName] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, name);
        toast.success('Account created! Welcome to OneHedge.');
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await sendOtp(phone, null);
      setOtpSent(true);
      toast.success('OTP sent! (Simulated: use 123456)');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(phone, null, otp, phoneName || 'User');
      toast.success('Verified! Welcome to OneHedge.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = async (provider) => {
    setLoading(true);
    try {
      const demoEmail = `demo.${provider}@onehedge.app`;
      await socialAuth(provider, demoEmail, `${provider} User`);
      toast.success(`Signed in with ${provider} (Simulated)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--oh-bg)] grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="auth-page">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <BrandLogo variant="auth" />
        </div>

        {/* Auth Card */}
        <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-8">
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="w-full bg-[var(--oh-bg)] border border-[var(--oh-border)] mb-6 h-12">
              <TabsTrigger value="email" className="flex-1 data-[state=active]:bg-[var(--oh-bg-tertiary)] data-[state=active]:text-white text-[var(--oh-text-muted)]" data-testid="auth-tab-email">
                <EnvelopeSimple size={16} className="mr-2" /> Email
              </TabsTrigger>
              <TabsTrigger value="phone" className="flex-1 data-[state=active]:bg-[var(--oh-bg-tertiary)] data-[state=active]:text-white text-[var(--oh-text-muted)]" data-testid="auth-tab-phone">
                <Phone size={16} className="mr-2" /> Phone
              </TabsTrigger>
              <TabsTrigger value="social" className="flex-1 data-[state=active]:bg-[var(--oh-bg-tertiary)] data-[state=active]:text-white text-[var(--oh-text-muted)]" data-testid="auth-tab-social">
                Social
              </TabsTrigger>
            </TabsList>

            {/* Email Tab */}
            <TabsContent value="email">
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Full Name</label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name"
                      className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12"
                      data-testid="auth-name-input" required />
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Email</label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com"
                    className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12"
                    data-testid="auth-email-input" required />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Password</label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                      className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12 pr-12"
                      data-testid="auth-password-input" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--oh-text-muted)] hover:text-white transition-colors">
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} data-testid="auth-submit-button"
                  className="w-full h-12 bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold tracking-wide hover:scale-[1.02] transition-transform shadow-[0_0_15px_rgba(0,255,163,0.2)]">
                  {loading ? 'Processing...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[var(--oh-text-secondary)] hover:text-[#00FFA3] transition-colors text-sm" data-testid="auth-toggle-mode">
                  {mode === 'login' ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              </div>
            </TabsContent>

            {/* Phone Tab */}
            <TabsContent value="phone">
              {!otpSent ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Phone Number</label>
                    <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+234 800 000 0000"
                      className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12"
                      data-testid="auth-phone-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Your Name</label>
                    <Input value={phoneName} onChange={e => setPhoneName(e.target.value)} placeholder="Enter your name"
                      className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12"
                      data-testid="auth-phone-name-input" />
                  </div>
                  <Button onClick={handleSendOtp} disabled={loading || !phone} data-testid="auth-send-otp-button"
                    className="w-full h-12 bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold tracking-wide hover:scale-[1.02] transition-transform">
                    {loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                  <p className="text-xs text-[var(--oh-text-muted)] text-center">Simulated: OTP will always be 123456</p>
                </div>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <p className="text-sm text-[var(--oh-text-secondary)] mb-2">Enter the 6-digit code sent to <span className="text-[#00FFA3]">{phone}</span></p>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)] mb-2 block">Verification Code</label>
                    <Input value={otp} onChange={e => setOtp(e.target.value)} placeholder="123456" maxLength={6}
                      className="bg-[var(--oh-bg)] border-[var(--oh-border)] focus:border-[#00FFA3] focus:ring-1 focus:ring-[#00FFA3] text-[var(--oh-text)] placeholder-[var(--oh-text-muted)] h-12 text-center font-mono text-xl tracking-[0.5em]"
                      data-testid="auth-otp-input" required />
                  </div>
                  <Button type="submit" disabled={loading || otp.length < 6} data-testid="auth-verify-otp-button"
                    className="w-full h-12 bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold tracking-wide hover:scale-[1.02] transition-transform">
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                  <button onClick={() => setOtpSent(false)} className="text-[var(--oh-text-secondary)] hover:text-[#00FFA3] transition-colors text-sm w-full text-center">
                    Change phone number
                  </button>
                </form>
              )}
            </TabsContent>

            {/* Social Tab */}
            <TabsContent value="social">
              <div className="space-y-3">
                <p className="text-xs text-[var(--oh-text-muted)] text-center mb-4">Simulated social sign-in for MVP</p>
                <Button onClick={() => handleSocial('google')} disabled={loading} variant="outline" data-testid="auth-google-button"
                  className="w-full h-12 bg-[var(--oh-bg)] border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#00B8FF] hover:bg-[rgba(0,184,255,0.05)] transition-colors">
                  <GoogleLogo size={20} weight="bold" className="mr-3" /> Continue with Google
                </Button>
                <Button onClick={() => handleSocial('twitter')} disabled={loading} variant="outline" data-testid="auth-twitter-button"
                  className="w-full h-12 bg-[var(--oh-bg)] border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#00B8FF] hover:bg-[rgba(0,184,255,0.05)] transition-colors">
                  <TwitterLogo size={20} weight="bold" className="mr-3" /> Continue with X (Twitter)
                </Button>
                <Button onClick={() => handleSocial('apple')} disabled={loading} variant="outline" data-testid="auth-apple-button"
                  className="w-full h-12 bg-[var(--oh-bg)] border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#00B8FF] hover:bg-[rgba(0,184,255,0.05)] transition-colors">
                  <AppleLogo size={20} weight="bold" className="mr-3" /> Continue with Apple
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-[var(--oh-text-muted)] text-xs text-center mt-6">
          By continuing, you agree to OneHedge <a href="/terms" className="text-[#00FFA3] hover:underline">Terms of Service</a> and <a href="/privacy" className="text-[#00FFA3] hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
